import type { FC, ReactNode } from 'react'
import type { BrandTone, Course } from '@/types'

import { ArrowRight, ArrowUpRight, BadgeCheck, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import Container from '@/components/landing/container'
import SectionHeading from '@/components/landing/section-heading'

import { COURSES } from '@/constants/landing'

interface CourseCardProps {
    course: Course
    index: number
}

const TONES: Record<BrandTone, { panel: string, ink: string, check: string }> = {
    indigo: {
        panel: 'bg-primary',
        ink: 'text-primary',
        check: 'bg-secondary text-primary'
    },
    teal: {
        panel: 'bg-brand-teal',
        ink: 'text-brand-teal-ink',
        check: 'bg-accent text-brand-teal-ink'
    },
    orange: {
        panel: 'bg-brand-orange',
        ink: 'text-brand-orange-ink',
        check: 'bg-brand-orange/12 text-brand-orange-ink'
    }
}

const CourseCard: FC<CourseCardProps> = ({ course, index }): ReactNode => {
    const tone = TONES[course.tone]
    const Icon = course.icon

    return (
        <article className='reveal group flex flex-col overflow-hidden rounded-[1.75rem] border border-border bg-card shadow-[0_1px_2px_rgba(16,22,90,0.05)] transition-[translate,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-30px_rgba(16,22,90,0.45)]'>
            <div className={cn('relative h-48 overflow-hidden', tone.panel)}>
                <div aria-hidden='true' className='absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.25)_1px,transparent_0)] bg-[length:16px_16px]' />

                <Icon
                    aria-hidden='true'
                    strokeWidth={1.25}
                    className='absolute -bottom-10 -right-8 size-52 text-white/15 transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-105'
                />

                <span aria-hidden='true' className='absolute right-6 top-5 font-heading text-sm font-semibold text-white/85'>
                    {`0${index + 1}`}
                </span>

                <div className='relative flex h-full flex-col justify-between p-6'>
                    <span className='inline-flex w-fit items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-foreground shadow-sm'>
                        <BadgeCheck className={cn('size-3.5', tone.ink)} aria-hidden='true' />
                        Placement assistance
                    </span>

                    <span className='grid size-14 place-items-center rounded-2xl bg-white shadow-lg'>
                        <Icon className={cn('size-7', tone.ink)} aria-hidden='true' />
                    </span>
                </div>
            </div>

            <div className='flex flex-1 flex-col p-6 sm:p-7'>
                <p className={cn('text-xs font-semibold uppercase tracking-[0.14em]', tone.ink)}>
                    {course.sector}
                </p>

                <h3 className='mt-2 text-2xl font-semibold text-foreground'>
                    {course.name}
                </h3>

                <p className='mt-3 leading-relaxed text-muted-foreground'>
                    {course.summary}
                </p>

                <ul className='mt-6 space-y-2.5' aria-label={`Where ${course.name} can take you`}>
                    {course.workplaces.map(place => (
                        <li key={place} className='flex items-center gap-3 text-[15px] font-medium text-foreground'>
                            <span className={cn('grid size-6 shrink-0 place-items-center rounded-full', tone.check)}>
                                <Check className='size-3.5' strokeWidth={3} aria-hidden='true' />
                            </span>
                            {place}
                        </li>
                    ))}
                </ul>

                <a
                    href={course.href}
                    target='_blank'
                    rel='noopener noreferrer'
                    className={cn('mt-auto inline-flex w-fit items-center gap-1.5 pt-8 text-[15px] font-semibold underline-offset-4 hover:underline', tone.ink)}
                >
                    View {course.name}
                    <ArrowUpRight className='size-4' aria-hidden='true' />
                    <span className='sr-only'>(opens in a new tab)</span>
                </a>
            </div>
        </article>
    )
}

const CoursesSection: FC = (): ReactNode => {
    return (
        <section id='courses' aria-labelledby='courses-title' className='py-20 sm:py-28'>
            <Container>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        id='courses-title'
                        eyebrow="Our courses"
                        title="Training built for the jobs that are hiring now"
                        description="Three focused courses for customer-facing corporate roles. Each blends online learning, practical training and live expert sessions, and ends with placement assistance."
                    />

                    <a
                        href='#tests'
                        className='group inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary lg:self-auto'
                    >
                        Not sure which course fits?
                        <span className='text-primary'>Take a test</span>
                        <ArrowRight className='size-4 text-primary transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                    </a>
                </div>

                <div className='mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
                    {COURSES.map((course, index) => (
                        <CourseCard key={course.id} course={course} index={index} />
                    ))}
                </div>
            </Container>
        </section>
    )
}

export default CoursesSection
